import type { AnalysisData } from '../types';

/** Knee flex vs hyperextension clinical mins from `evaluation-rules.yaml` `posture.thresholds`. */
export interface LateralKneeClassificationThresholds {
  kneeFlexionDegMin: number;
  kneeHyperextensionDegMin: number;
}

export interface ParsedLateralPostureMetrics {
  viewSide: 'Left' | 'Right' | null;
  lateralViews: ('Left' | 'Right')[];
  pelvicTilt: number;
  hipFwdVal: number;
  shoFwdVal: number;
  sagittalTorsoOffset: number;
  kneeEntries: { kVal: number; kStr: string }[];
  maxKneeFlexDeg: number;
  maxKneeHyperextDeg: number;
  isKneeFlexed: boolean;
  isKneeHyperext: boolean;
  isKneeNeutral: boolean;
  avgCvAngle: number;
  tibialInclination: number;
  /** Knee-Hip Offset, anterior-positive cm (knee anterior to hip > 0). Averaged across lateral views. */
  kneeHipAntCm: number;
  isPelvicTiltEstimated: boolean;
}

const normalizeKey = (s: string): string => String(s).toLowerCase().replace(/[^a-z0-9]/g, '');

function getRawValue(
  analysisData: AnalysisData,
  view: 'Left' | 'Right' | 'Anterior' | 'Posterior',
  labelIncludes: string
): number {
  const viewData = analysisData[view];
  if (!viewData) return NaN;
  const needle = normalizeKey(labelIncludes);
  const key = Object.keys(viewData).find((k) => normalizeKey(k).includes(needle));
  if (!key) return NaN;
  const value = viewData[key as keyof typeof viewData];
  const match = String(value).match(/-?\d+(\.\d+)?/);
  if (!match) return NaN;
  const parsedVal = parseFloat(match[0]);
  const lowerValStr = String(value).toLowerCase();
  if (
    !lowerValStr.includes('°') &&
    !lowerValStr.includes('deg') &&
    lowerValStr.includes('in')
  ) {
    return parsedVal * 2.54;
  }
  return parsedVal;
}

function getKeyString(
  analysisData: AnalysisData,
  view: 'Left' | 'Right' | 'Anterior' | 'Posterior',
  labelIncludes: string
): string {
  const viewData = analysisData[view];
  if (!viewData) return '';
  const needle = normalizeKey(labelIncludes);
  const key = Object.keys(viewData).find((k) => normalizeKey(k).includes(needle));
  if (!key) return '';
  return String(viewData[key as keyof typeof viewData]);
}

function isAnteriorShift(
  analysisData: AnalysisData,
  view: 'Left' | 'Right' | 'Anterior' | 'Posterior',
  labelIncludes: string
): boolean {
  const valueStr = getKeyString(analysisData, view, labelIncludes);
  return valueStr.includes('Ant') || valueStr.includes('Fwd') || valueStr.includes('FWD');
}

function getSignedFromDirection(
  analysisData: AnalysisData,
  view: 'Left' | 'Right' | 'Anterior' | 'Posterior',
  label: string,
  positiveToken: string,
  negativeToken: string
): number {
  const mag = getRawValue(analysisData, view, label);
  if (!isFinite(mag)) return NaN;
  const s = getKeyString(analysisData, view, label);
  if (s.includes(positiveToken)) return mag;
  if (s.includes(negativeToken)) return -mag;
  return mag;
}

/**
 * Parses lateral / sagittal-related quantities from serialized per-view metric strings.
 */
export function parseLateralPostureMetrics(
  analysisData: AnalysisData,
  kneeThresholds: LateralKneeClassificationThresholds
): ParsedLateralPostureMetrics | null {
  const hasLeft = !!analysisData['Left'];
  const hasRight = !!analysisData['Right'];
  const lateralViews: ('Left' | 'Right')[] = [];
  if (hasLeft) lateralViews.push('Left');
  if (hasRight) lateralViews.push('Right');
  const viewSide: 'Left' | 'Right' | null = hasRight ? 'Right' : hasLeft ? 'Left' : null;
  if (!viewSide) return null;

  const pelvicTilt = getSignedFromDirection(analysisData, viewSide, 'Pelvic Tilt', 'Ant', 'Post');
  const pelvicTiltStr = getKeyString(analysisData, viewSide, 'Pelvic Tilt');
  const isPelvicTiltEstimated = pelvicTiltStr.includes('Est') || pelvicTiltStr.includes('est');

  const getSignedShift = (view: 'Left' | 'Right', label: string): number => {
    const mag = getRawValue(analysisData, view, label);
    if (!isFinite(mag)) return NaN;
    return isAnteriorShift(analysisData, view, label) ? mag : -mag;
  };

  const signedHipShifts = lateralViews
    .map((view) => getSignedShift(view, 'Hip Anterior Shift'))
    .filter((v) => isFinite(v)) as number[];
  const signedShoulderShifts = lateralViews
    .map((view) => getSignedShift(view, 'Shoulder Anterior Shift'))
    .filter((v) => isFinite(v)) as number[];

  const hipFwdVal = signedHipShifts.length
    ? signedHipShifts.reduce((a, b) => a + b, 0) / signedHipShifts.length
    : 0;
  const shoFwdVal = signedShoulderShifts.length
    ? signedShoulderShifts.reduce((a, b) => a + b, 0) / signedShoulderShifts.length
    : 0;

  const kneeEntries = lateralViews
    .map((view) => {
      const kVal = getRawValue(analysisData, view, 'Knee State');
      const kStr = getKeyString(analysisData, view, 'Knee State');
      return { kVal, kStr };
    })
    .filter((entry) => isFinite(entry.kVal) && entry.kStr) as { kVal: number; kStr: string }[];

  const flexEntries = kneeEntries.filter((entry) => entry.kStr.includes('Flex'));
  const hyperextEntries = kneeEntries.filter((entry) => entry.kStr.includes('Hyperext'));
  const maxKneeFlexDeg = flexEntries.length
    ? Math.max(...flexEntries.map((entry) => entry.kVal))
    : NaN;
  const maxKneeHyperextDeg = hyperextEntries.length
    ? Math.max(...hyperextEntries.map((entry) => entry.kVal))
    : NaN;

  const flexMin = kneeThresholds.kneeFlexionDegMin;
  const hyperMin = kneeThresholds.kneeHyperextensionDegMin;
  const isKneeFlexed = kneeEntries.some(
    (entry) => entry.kStr.includes('Flex') && entry.kVal > flexMin
  );
  const isKneeHyperext = kneeEntries.some(
    (entry) => entry.kStr.includes('Hyperext') && entry.kVal >= hyperMin
  );
  const isKneeNeutral = !isKneeFlexed && !isKneeHyperext;

  const cvAngleEntries = lateralViews
    .map((view) => getRawValue(analysisData, view, 'Craniovertebral'))
    .filter((val) => isFinite(val)) as number[];
  const avgCvAngle = cvAngleEntries.length
    ? cvAngleEntries.reduce((a, b) => a + Math.abs(b), 0) / cvAngleEntries.length
    : NaN;

  const tibialEntries = lateralViews
    .map((view) => getSignedFromDirection(analysisData, view, 'Tibial Inclination', 'Ant', 'Post'))
    .filter((val) => isFinite(val)) as number[];
  const tibialInclination = tibialEntries.length
    ? tibialEntries.reduce((a, b) => a + b, 0) / tibialEntries.length
    : NaN;

  // Knee-Hip Offset: "Ant" = knee anterior to hip (positive), "Post" = posterior (negative).
  const kneeHipEntries = lateralViews
    .map((view) => getSignedFromDirection(analysisData, view, 'Knee-Hip Offset', 'Ant', 'Post'))
    .filter((val) => isFinite(val)) as number[];
  const kneeHipAntCm = kneeHipEntries.length
    ? kneeHipEntries.reduce((a, b) => a + b, 0) / kneeHipEntries.length
    : NaN;

  return {
    viewSide,
    lateralViews,
    pelvicTilt,
    hipFwdVal,
    shoFwdVal,
    sagittalTorsoOffset: shoFwdVal - hipFwdVal,
    kneeEntries,
    maxKneeFlexDeg,
    maxKneeHyperextDeg,
    isKneeFlexed,
    isKneeHyperext,
    isKneeNeutral,
    avgCvAngle,
    tibialInclination,
    kneeHipAntCm,
    isPelvicTiltEstimated,
  };
}

export function getFrontalView(analysisData: AnalysisData): 'Anterior' | 'Posterior' {
  return analysisData['Posterior'] ? 'Posterior' : analysisData['Anterior'] ? 'Anterior' : 'Anterior';
}

/**
 * Coronal metrics for lateralization (person-relative: signs match `fmtSignedDeg` /
 * `analyzeAnterior` raw conventions — not raw image x without direction).
 *
 * Prefer **Anterior** when present: `analyzePosterior` omits head tilt and pelvic shift,
 * and ear/level/shift should agree on one person-facing capture.
 */
export function getFrontalCoronalMetrics(analysisData: AnalysisData, frontalView: 'Anterior' | 'Posterior') {
  const hasAnterior = !!analysisData['Anterior'];
  const coronalView: 'Anterior' | 'Posterior' = hasAnterior ? 'Anterior' : frontalView;

  return {
    headTilt: getSignedFromDirection(analysisData, coronalView, 'Head tilt', 'Right', 'Left'),
    shoulderLevel: getSignedFromDirection(analysisData, coronalView, 'Shoulders level', 'Right', 'Left'),
    hipLevel: getSignedFromDirection(analysisData, coronalView, 'Pelvis level', 'Right', 'Left'),
    pelvicShift: getSignedFromDirection(analysisData, coronalView, 'Pelvic shift', 'Left', 'Right'),
  };
}

export { getRawValue, getKeyString, getSignedFromDirection };
